import { memo, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { TooltipSurface } from "components/common/TooltipSurface";
import { getLayoutTooltipContent } from "./getLayoutTooltipContent";
import { getTooltipTransform } from "./getTooltipTransform";
import type { LayoutTooltipProps } from "./types";

const transformAt = (x: number, y: number) =>
  getTooltipTransform({
    x,
    y,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
  });

// Mounted where the cursor entered the shape, then follows it by writing its own transform: no React work per move.
export const LayoutTooltip = memo(function LayoutTooltip({
  tooltip,
  x,
  y,
}: LayoutTooltipProps) {
  const { t } = useTranslation();
  const surfaceRef = useRef<HTMLSpanElement>(null);

  const { title, lines } = useMemo(
    () => getLayoutTooltipContent({ tooltip, t }),
    [tooltip, t],
  );

  useEffect(() => {
    const follow = (event: MouseEvent) => {
      if (!surfaceRef.current) return;
      surfaceRef.current.style.transform = transformAt(
        event.clientX,
        event.clientY,
      );
    };
    document.addEventListener("mousemove", follow);
    return () => document.removeEventListener("mousemove", follow);
  }, []);

  return createPortal(
    <TooltipSurface
      ref={surfaceRef}
      className="z-50 max-w-[320px]"
      style={{
        position: "fixed",
        left: 0,
        top: 0,
        transform: transformAt(x, y),
      }}
    >
      <span className="block font-semibold">{title}</span>
      {lines.map((line) => (
        <span key={line} className="block font-normal text-slate-300">
          {line}
        </span>
      ))}
    </TooltipSurface>,
    document.body,
  );
});
